const img = (id, w = 1200) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

export const IMAGES = {
  heroMain: img("photo-1613545325278-f24b0cae1224", 1400),
  heroSecondary: img("photo-1560448204-e02f11c3d0e2", 800),
  hdb: img("photo-1565967511849-76a60a516170"),
  pineapple: "/assets/lobang-logo.jpg",
  skyline:
    "https://images.pexels.com/photos/22743872/pexels-photo-22743872.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export const MARQUEE_ITEMS = [
  "Hollow-Tile Tapping",
  "Water Ponding Tests",
  "Blue-Tape Defect Marking",
  "Window & Door Checks",
  "Electrical Point Testing",
  "BSC Joint Inspection",
];

export const SERVICES = [
  {
    id: "hollow-tile-tapping",
    title: "Hollow-Tile Tapping",
    tagline: "Every tile, tapped and heard.",
    desc: "Using a professional T-bar, we tap every floor and wall tile in your home. A hollow ring means poor cement adhesion — tiles that can pop, crack or debond within months of moving in.",
    checks: [
      "Every floor & wall tile tapped individually",
      "Hollow tiles flagged with blue tape on the spot",
      "Skirting, kerbs and bathroom tile walls covered",
      "Defect locations mapped in your photo report",
      "Severity graded for BSC rectification priority",
    ],
    image: img("photo-1600607687939-ce8a6c25118c"),
  },
  {
    id: "water-ponding",
    title: "Water Ponding Tests",
    tagline: "Where water sits, trouble follows.",
    desc: "We flood your toilets, bathrooms, kitchen yard and balcony to verify floor gradients and drainage. Standing water breeds mosquitoes, stains tiles and seeps into the unit below.",
    checks: [
      "Toilets, baths, kitchen & balcony flood-tested",
      "Floor gradient and drainage flow verified",
      "Ponding depth and drain-down time recorded",
      "Early seepage signs checked at walls & ceilings",
      "Photo evidence captured for BSC submission",
    ],
    image: img("photo-1552321554-5fefe8c9ef14"),
  },
  {
    id: "blue-tape-marking",
    title: "Blue-Tape Defect Marking",
    tagline: "If it's flawed, it's flagged.",
    desc: "Uneven walls, hairline cracks, chips, paint runs and cosmetic flaws — across every room, door and toilet — are marked with signature painter's blue tape and catalogued in your report.",
    checks: [
      "Wall evenness checked with straightedge & laser",
      "Hairline vs structural cracks distinguished",
      "Paint defects, stains, chips and rough patches",
      "Doors, frames, toilets & fittings inspected",
      "Every defect taped and photographed in context",
    ],
    image: img("photo-1562259949-e8e7689d7828"),
  },
  {
    id: "window-checks",
    title: "Window Checks",
    tagline: "Smooth glide, sealed tight.",
    desc: "Windows are your home's first defence against Singapore's driving rain. We test every panel for alignment, smooth operation, seal integrity and safety mechanisms.",
    checks: [
      "Every panel opened, slid and locked repeatedly",
      "Alignment and level verified panel by panel",
      "Rubber gaskets and weather seals inspected",
      "Safety stoppers and restrictors tested",
      "Seepage risk at frames and sills assessed",
    ],
    image: img("photo-1493809842364-78817add7ffb"),
  },
  {
    id: "electrical-testing",
    title: "Electrical Point Testing",
    tagline: "Every point, proven live.",
    desc: "From 13A sockets to USB outlets, lights to LAN ports — we functionally test every electrical point in your home so nothing is dead on move-in day.",
    checks: [
      "All 13A sockets tested with a socket tester",
      "USB charging points verified under load",
      "Every light point and switch cycled",
      "LAN / data port continuity checked",
      "DB box labelling and water heater points reviewed",
    ],
    image: img("photo-1621905251189-08b45d6a269e"),
  },
];

export const PROCESS = [
  {
    chapter: "01",
    title: "The Defect-Check Visit",
    subtitle: "First comprehensive inspection",
    desc: "A meticulous room-by-room audit of your entire unit. Hollow tiles, ponding, uneven walls, cracks, windows, doors and every electrical point — checked, blue-taped and photographed.",
    points: ["2–4 hours depending on flat size", "Signature blue-tape marking on-site", "Digital photo report within 24 hours"],
    image: img("photo-1504307651254-35680f356dfd"),
  },
  {
    chapter: "02",
    title: "Joint Inspection with BSC",
    subtitle: "We face the developer with you",
    desc: "We walk the unit together with the developer's Building Service Centre (BSC) manager, present the defect report and make sure every flagged item is formally acknowledged for rectification.",
    points: ["We represent your interests on-site", "Report presented & agreed item-by-item", "Rectification scope confirmed in writing"],
    image: img("photo-1600880292203-757bb62b4baf"),
  },
  {
    chapter: "03",
    title: "Post-Rectification Verification",
    subtitle: "Trust, but verify",
    desc: "After the contractor's rectification works, we return to re-test every flagged defect. Only when each item passes do we sign your home off as renovation-ready.",
    points: ["Every earlier defect re-tested", "New issues from rectification flagged", "Final sign-off before renovation"],
    image: img("photo-1600585154340-be6161a56a0c"),
  },
];

export const PRICING_BTO = [
  { type: "2-Room BTO", oneTrip: 108, threeTrip: 208 },
  { type: "3-Room BTO", oneTrip: 158, threeTrip: 258 },
  { type: "4-Room BTO", oneTrip: 188, threeTrip: 288 },
  { type: "5-Room BTO", oneTrip: 208, threeTrip: 328 },
  { type: "3Gen Flat", oneTrip: 228, threeTrip: 368 },
];

export const PRICING_CONDO = [
  { type: "1–600 sq ft", oneTrip: 178, threeTrip: 278 },
  { type: "601–1,000 sq ft", oneTrip: 228, threeTrip: 328 },
  { type: "1,001–1,300 sq ft", oneTrip: 238, threeTrip: 388 },
  { type: "1,301–1,700 sq ft", oneTrip: 338, threeTrip: 418 },
  { type: "1,701–2,000 sq ft", oneTrip: 388, threeTrip: 468 },
  { type: "2,001 sq ft onwards", oneTrip: "POA", threeTrip: "POA" },
];

export const TRIP_FEATURES = {
  one: {
    name: "One-Trip Essential",
    tagline: "The single deep-dive",
    features: [
      "One comprehensive defect-check visit",
      "Full 5-pillar inspection scope",
      "Blue-tape marking of all defects",
      "Digital photo report within 24 hours",
      "Ready for your own BSC submission",
    ],
  },
  three: {
    name: "Three-Trip Complete",
    tagline: "Full journey, zero worry",
    features: [
      "Everything in One-Trip Essential",
      "Joint inspection with the BSC manager",
      "Post-rectification verification visit",
      "Re-testing of every flagged defect",
      "Sign-off before renovation begins",
    ],
  },
};

export const FAQS = [
  {
    q: "What exactly does a Lobang inspection cover?",
    a: "Five pillars: hollow-tile tapping on every tile, water ponding tests in all wet areas, blue-tape marking of uneven walls, cracks and cosmetic defects across rooms, doors and toilets, full window checks, and functional testing of every electrical point — sockets, USB outlets, lights and LAN ports.",
  },
  {
    q: "How long does the first visit take?",
    a: "Typically 2 to 4 hours depending on your flat size. A 3-room BTO usually takes around 2 hours; larger condos and executive flats can take up to 4. We never rush — every tile and every point gets checked.",
  },
  {
    q: "What's the difference between One-Trip and Three-Trip?",
    a: "One-Trip is a single comprehensive defect check with a full photo report — ideal if you want to submit defects to the BSC yourself. Three-Trip covers the whole journey: the defect-check visit, a joint inspection with the BSC manager, and a post-rectification verification visit before you renovate.",
  },
  {
    q: "When should I book my inspection?",
    a: "As soon as you collect your keys, and before any renovation or defect-rectification window closes. HDB and developers have strict defect liability periods, so the earlier we inspect, the more the developer must fix at no cost to you.",
  },
  {
    q: "Do you deal with the BSC or developer for me?",
    a: "Yes — on the Three-Trip package we attend the joint inspection with the Building Service Centre (BSC) manager, present the report, and ensure every flagged defect is acknowledged and scheduled for rectification.",
  },
  {
    q: "What will I receive after the inspection?",
    a: "A digital photo report within 24 hours, documenting every defect with location, photos and severity — mapped to the blue-tape markings left in your unit so the BSC can find and fix each item quickly.",
  },
  {
    q: "Do you inspect condos and landed homes too?",
    a: "Absolutely. We inspect new BTO HDB flats, condominiums and landed properties across Singapore. Landed homes are quoted individually based on floor area and storey count — just WhatsApp us your address.",
  },
  {
    q: "How do I book?",
    a: "Simply WhatsApp us at +65 8490 0316 with your key collection date, flat type and address. We'll confirm your slot, usually within a few hours.",
  },
];

export const STATS = [
  { value: "350+", label: "Homes inspected" },
  { value: "5", label: "Inspection pillars" },
  { value: "24h", label: "Report turnaround" },
  { value: "3", label: "Trips, fully covered" },
];
